import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { SpeechRecognitionService } from './speech-recognition.service';

@Component({
    selector: 'app-reactive-dictado',
    standalone: false,
    templateUrl: './dictado.component.html',
    styleUrls: ['./dictado.component.scss']
})
export class ReactiveDictadoComponent implements OnInit, OnDestroy {
    @Input() public transcriptionLabel = '';
    @Input() public speechTranscription = '';

    @Output() public sendTranscription: EventEmitter<string> = new EventEmitter<string>();

    public isRecording = false;

    private readonly destroy$ = new Subject<void>();

    constructor(public readonly speechRecognitionService: SpeechRecognitionService) {}

    /**
     * Initializes speech recognition and subscribes to transcription updates.
     */
    public ngOnInit(): void {
        this.speechRecognitionService.init();

        this.speechRecognitionService.transcriptedSpeechObserver
            .pipe(takeUntil(this.destroy$))
            .subscribe((transcriptedSpeech: string) => {
                this.speechTranscription += transcriptedSpeech;
                this.sendTranscription.emit(this.speechTranscription);
            });
    }

    public ngOnDestroy(): void {
        if (this.isRecording) {
            this.stopRecognition();
        }

        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Starts speech recognition if the browser supports it.
     */
    public start(): void {
        if (!this.speechRecognitionService.recognition) {
            this.speechRecognitionService.transcriptionError = 'El reconocimiento de voz no está inicializado.';
            return;
        }

        this.speechRecognitionService.speechTranscripted = '';
        this.speechRecognitionService.transcriptionError = '';
        this.speechRecognitionService.transcriptionInfo = 'Escuchando...';

        try {
            this.speechRecognitionService.recognition.start();
            this.isRecording = true;
        } catch (error) {
            this.speechRecognitionService.transcriptionError = this.getSpeechRecognitionError(error);
            this.isRecording = false;
        }
    }

    /**
     * Stops speech recognition.
     */
    public stop(): void {
        this.stopRecognition();
    }

    /**
     * Clears the transcripted text from the textarea.
     */
    public deleteTranscription(): void {
        this.speechTranscription = '';
        this.sendTranscription.emit(this.speechTranscription);
        this.isRecording = false;
        this.speechRecognitionService.transcriptionInfo = '';
        this.speechRecognitionService.transcriptionError = '';
    }

    private stopRecognition(): void {
        if (!this.speechRecognitionService.recognition) {
            this.speechRecognitionService.transcriptionError = 'El reconocimiento de voz no está inicializado.';
            this.isRecording = false;
            return;
        }

        try {
            this.speechRecognitionService.recognition.stop();
        } catch (error) {
            this.speechRecognitionService.transcriptionError = this.getSpeechRecognitionError(error);
        } finally {
            this.isRecording = false;
        }
    }

    private getSpeechRecognitionError(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return `Error durante el reconocimiento de voz: ${error.message}`;
        }

        return 'Error durante el reconocimiento de voz.';
    }
}
